import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import { Category, Filters, Product } from "@/models/category";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { AppState } from "..";
import { getCookie } from "cookies-next";
import qs from "qs";
import UAParser from "ua-parser-js";
import { unbxdConfigEvent, unbxdTrackEvent } from "@/lib/unbxd";

export interface CategoryState {
  data: Category | null;
  mapping: { id: number; name: string; slug: string }[];
  filters: Filters;
  similarFilters: Filters;
  facets: any;
  similarProducts: any;
  products: {
    filters: any;
    total: any;
    facets: any;
    data: Product[];
    result: any;
    page: number;
    size: number;
    totalPages: number;
    totalRecords: number;
    response: any;
  } | null;
  productsLoading: boolean;
  mappingLoading: boolean;
}

const initialState: CategoryState = {
  data: null,
  similarProducts: null,
  similarFilters: { sizes: [], purities: [], occasions: [], categories: [] },
  mapping: [],
  facets: null,
  filters: { sizes: [], purities: [], occasions: [], categories: [] },
  products: null,
  productsLoading: true,
  mappingLoading: true,
};
let UNBXD_DEVICE = "";
if (typeof window !== "undefined" && typeof navigator !== "undefined") {
  const { browser, device, os } = UAParser(navigator.userAgent);
  UNBXD_DEVICE = JSON.stringify({
    type: device.type || "desktop",
    os: os.name,
    source: browser?.name,
  });
}
export const fetchCategoryBySlug = createAsyncThunk(
  "category/fetchCategoryBySlug",
  async (slug: string) => {
    const { data } = await httpClient.get(endpoints.category.detail(slug));

    return data;
  }
);

const categoryTreePathCache: Record<string, string> = {};
const resolveCategoryPath = async (
  categoryId: string | undefined,
  fallback: string | undefined
) => {
  if (!categoryId || categoryId === "undefined") return fallback;
  if (categoryTreePathCache[categoryId]) return categoryTreePathCache[categoryId];
  try {
    const { data } = await httpClient.get(endpoints.category.tree(categoryId));
    if (data?.path) {
      categoryTreePathCache[categoryId] = data.path;
      return data.path as string;
    }
  } catch (err) {
    console.log("category tree", err);
  }
  return fallback;
};

export const fetchCategoryMappingById = createAsyncThunk(
  "category/fetchCategoryMappingById",
  async (id: number) => {
    const { data } = await httpClient.get(endpoints.category.mapping(id));
    return data;
  }
);

export const fetchCategoryFiltersById = createAsyncThunk(
  "category/fetchCategoryFiltersById",
  async (id: number) => {
    const { data } = await httpClient.get(endpoints.products.filters(id));
    return data;
  }
);

export const fetchCategoryProductsById = createAsyncThunk(
  "category/fetchCategoryProductsById",
  async (params: any = {}) => {
    const { data } = await httpClient.get(
      endpoints.products.category(params.id),
      {
        params: params.query,
      }
    );
    return data;
  }
);

export const fetchLoadMoreCategoryProductsById = createAsyncThunk(
  "category/fetchLoadMoreCategoryProductsById",
  async (params: any = {}) => {
    const { data } = await httpClient.get(
      endpoints.products.category(params.id),
      {
        params: params.query,
      }
    );
    return data;
  }
);
export const fetchSimilarProducts = createAsyncThunk(
  "category/fetchSimilarProducts",
  async (params: any = {}) => {
    const { data } = await httpClient.get(endpoints.products.search(), {
      params: params.query,
    });

    return data;
  }
);

export const fetchSimilarProductFilters = createAsyncThunk(
  "category/fetchSimilarProductFilters",
  async (params: any = {}) => {
    const { data } = await httpClient.get(endpoints.products.searchFilters(), {
      params: params.query,
    });
    return data;
  }
);

export const fetchSearchProducts = createAsyncThunk(
  "category/fetchSearchProducts",
  async (params: any = {}) => {
    const { data } = await httpClient.get(endpoints.products.search(), {
      params: params.query,
    });

    return data;
  }
);
export const fetchMoreSearchProducts = createAsyncThunk(
  "category/fetchMoreSearchProducts",
  async (params: any = {}) => {
    const { data } = await httpClient.get(endpoints.products.search(), {
      params: params.query,
    });
    return data;
  }
);

export const fetchSearchFilters = createAsyncThunk(
  "category/fetchSearchFilters",
  async (params: any = {}) => {
    const { data } = await httpClient.get(endpoints.products.searchFilters(), {
      params: params.query,
    });
    return data;
  }
);

// -----***wizzy***-----
export const fetchSearchProductBywizzy = createAsyncThunk(
  "category/fetchSearchProductBywizzy",
  async (params: any = {}) => {
    const { data } = await httpClient.post(
      endpoints.products.searchWizzy(),
      {
        q: params.query?.search,
        sort: params.query?.sort
          ? JSON.stringify(params.query.sort)
          : undefined,
        facets: JSON.stringify([
          { key: "purity" },
          { key: "occasions" },
          { key: "categories" },
        ]),
      },
      {
        headers: {
          "x-store-id": `${process.env.NEXT_PUBLIC_WIZZY_STOREID}`,
          "x-api-key": `${process.env.NEXT_PUBLIC_WIZZY_APIKEY}`,
        },
      }
    );
    return data;
  }
);
export const fetchCategoryFiltersByIdWizzy = createAsyncThunk(
  "category/fetchCategoryFiltersByIdWizzy",
  async (params: any = {}) => {
    const { data } = await httpClient.post(
      endpoints.products.searchMoreWizzy(),
      {
        q: params.query?.search,
        filters: JSON.stringify(params.query?.filters),
      },
      {
        headers: {
          "x-store-id": `${process.env.NEXT_PUBLIC_WIZZY_STOREID}`,
          "x-api-key": `${process.env.NEXT_PUBLIC_WIZZY_APIKEY}`,
        },
      }
    );
    return data;
  }
);

export const fetchMoreSearchProductsByWizzy = createAsyncThunk(
  "category/fetchMoreSearchProductsByWizzy",
  async (params: any = {}) => {
    const { data } = await httpClient.post(
      endpoints.products.searchMoreWizzy(),
      { filters: JSON.stringify(params.query?.filters) },
      {
        headers: {
          "x-store-id": `${process.env.NEXT_PUBLIC_WIZZY_STOREID}`,
          "x-api-key": `${process.env.NEXT_PUBLIC_WIZZY_APIKEY}`,
        },
      }
    );
    return data;
  }
);

export const fetchLoadMoreCategoryProductsByIdWizzy = createAsyncThunk(
  "category/fetchLoadMoreCategoryProductsByIdWizzy",
  async (params: any = {}) => {
    const { data } = await httpClient.post(
      endpoints.products.searchMoreWizzy(),
      { filters: JSON.stringify(params.query?.filters) },
      {
        headers: {
          "x-store-id": `${process.env.NEXT_PUBLIC_WIZZY_STOREID}`,
          "x-api-key": `${process.env.NEXT_PUBLIC_WIZZY_APIKEY}`,
        },
      }
    );
    return data;
  }
);
// -----***wizzy***-----

// -----***unbxd***-----
export const fetchSearchProductByunbxd = createAsyncThunk(
  "category/fetchSearchProductByunbxd",
  async (params: any = {}) => {
    try {
      const data = await httpClient.get(endpoints.products.searchUnbxd(), {
        params: {
          rows: 20,

          uid: getCookie("unbxd.userId") || "uid-1466015353887-20419",
          "user-type": "repeat",
          page: 1,
          q: params.query?.search || "",
          "facet.multiselect": true,
          sort: params.query?.sort ? params.query?.sort : undefined,
          filter: [
            params.query?.sellingPrice
              ? `sellingPrice:[${params.query.sellingPrice?.gte} TO ${params.query.sellingPrice?.lte}]`
              : undefined,

            params.query?.purity
              ? params.query.purity
                  .map((v: any) => `vPurity_uFilter:"${v}"`)
                  .join(" OR ")
              : undefined,
            params.query?.fast_delivery !== undefined
              ? `vIsFastDelivery_uFilter:\"${params.query.fast_delivery}\"`
              : undefined,
            params.query?.try_online !== undefined
              ? `vTryItOnline_uFilter:\"${params.query.try_online}\"`
              : undefined,
            params.query?.occasions
              ? params.query.occasions
                  .map((v: any) => `vOccasions_uFilter:"${v}"`)
                  .join(" OR ")
              : undefined,
          ].filter(Boolean),
          fields: [
            "title",
            "sku",
            "varientId",
            "productUrl",
            "imageUrl",
            "hasTryOnline",
            "discount",
            "discountLabel",
            "isBestSeller",
            "isNewArrival",
            "price",
            "sellingPrice",
            "defaultVariantId",
            "productId",
            "vIsFastDelivery",
          ].join(","),
        },
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
        headers: {
          "unbxd-device-type": UNBXD_DEVICE,
        },
      });

      return data;
    } catch (err) {
      console.log("....", err);
    }
  }
);

export const fetchCategoryFiltersByUnbxd = createAsyncThunk(
  "category/fetchCategoryFiltersByUnbxd",
  async (params: any = {}) => {
    try {
      setTimeout(() => {
        unbxdConfigEvent(`categoryPathId:${params.query.categories}`);
      }, 1000);
      const categoryPath = params.query.categoryPath
        ? await resolveCategoryPath(
            params.query.categories,
            params.query.categoryPath
          )
        : undefined;
      const { data } = await httpClient.get(
        endpoints.products.categoryUnbxd(),
        {
          params: {
            rows: 20,
            uid: getCookie("unbxd.userId") || "uid-1466015353887-20419",
            // "user-type": "repeat",
            page: 1,
            ...(params.query.categories && {
              "p-id": `categoryPathId:${params.query.categories}`,
            }),
            ...(params.query.try_online && { p: 'hasTryOnline:"true"' }),
            ...(categoryPath && {
              "category-filter": categoryPath,
            }),

            // "p-id": `categoryPathId:${params.query.categories}`,
            "facet.multiselect": true,
            selectedfacet: true,
            pagetype: "boolean",
            sort: params.query?.sort ? params.query?.sort : undefined,
            // variants: false,
            filter: [
              params.query?.sellingPrice
                ? `sellingPrice:[${params.query.sellingPrice?.gte} TO ${params.query.sellingPrice?.lte}]`
                : undefined,

              params.query?.purity
                ? params.query.purity
                    .map((v: any) => `vPurity_uFilter:"${v}"`)
                    .join(" OR ")
                : undefined,
              params.query?.fast_delivery !== undefined
                ? `vIsFastDelivery_uFilter:\"${params.query.fast_delivery}\"`
                : undefined,
              // params.query?.try_online !== undefined
              //   ? `vTryItOnline_uFilter:\"${params.query.try_online}\"`
              //   : undefined,
              params.query?.occasions
                ? params.query.occasions
                    .map((v: any) => `vOccasions_uFilter:"${v}"`)
                    .join(" OR ")
                : undefined,
            ].filter(Boolean),
            fields: [
              "title",
              "sku",
              "varientId",
              "productUrl",
              "imageUrl",
              "hasTryOnline",
              "discount",
              "discountLabel",
              "isBestSeller",
              "isNewArrival",
              "price",
              "sellingPrice",
              "defaultVariantId",
              "productId",
              "vIsFastDelivery",
            ].join(","),
          },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
          headers: {
            "unbxd-device-type": UNBXD_DEVICE,
          },
        }
      );

      return data;
    } catch (err) {
      console.log("....", err);
    }
  }
);

export const fetchMoreSearchProductsByUnbxd = createAsyncThunk(
  "category/fetchMoreSearchProductsByUnbxd",
  async (params: any = {}) => {
    const data = await httpClient.get(endpoints.products.searchUnbxd(), {
      params: {
        rows: 20,

        uid: getCookie("unbxd.userId") || "uid-1466015353887-20419",
        "user-type": "repeat",
        q: params.query?.search || "",
        page: params.query?.page,
        "facet.multiselect": true,
        sort: params.query?.sort ? params.query?.sort : undefined,
        filter: [
          params.query?.sellingPrice
            ? `sellingPrice:[${params.query.sellingPrice?.gte} TO ${params.query.sellingPrice?.lte}]`
            : undefined,

          params.query?.purity
            ? params.query.purity
                .map((v: any) => `vPurity_uFilter:"${v}"`)
                .join(" OR ")
            : undefined,
          params.query?.fast_delivery !== undefined
            ? `vIsFastDelivery_uFilter:\"${params.query.fast_delivery}\"`
            : undefined,
          params.query?.try_online !== undefined
            ? `vTryItOnline_uFilter:\"${params.query.try_online}\"`
            : undefined,
          params.query?.occasions
            ? params.query.occasions
                .map((v: any) => `vOccasions_uFilter:"${v}"`)
                .join(" OR ")
            : undefined,
        ].filter(Boolean),
        fields: [
          "title",
          "sku",
          "varientId",
          "productUrl",
          "imageUrl",
          "hasTryOnline",
          "discount",
          "discountLabel",
          "isBestSeller",
          "isNewArrival",
          "price",
          "sellingPrice",
          "defaultVariantId",
          "productId",
          "vIsFastDelivery",
        ].join(","),
      },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat" }),

      headers: {
        "unbxd-device-type": UNBXD_DEVICE,
      },
    });
    return data;
  }
);

export const fetchMoreCategoryProductsByUnbxd = createAsyncThunk(
  "category/fetchLoadMoreCategoryProductsByUnbxd",
  async (params: any = {}) => {
    setTimeout(() => {
      unbxdConfigEvent(`categoryPathId:${params.query.categories}`);
    }, 1000);
    const categoryPath = params.query.categoryPath
      ? await resolveCategoryPath(
          params.query.categories,
          params.query.categoryPath
        )
      : undefined;

    const { data } = await httpClient.get(endpoints.products.categoryUnbxd(), {
      params: {
        rows: 20,

        uid: getCookie("unbxd.userId") || "uid-1466015353887-20419",
        // "user-type": "repeat",
        page: params.query?.page,
        ...(params.query.categories && {
          "p-id": `categoryPathId:${params.query.categories}`,
        }),
        ...(params.query.try_online && { p: 'hasTryOnline:"true"' }),
        ...(categoryPath && {
          "category-filter": categoryPath,
        }),

        // "p-id": `categoryPathId:${params.query.categories}`,
        "facet.multiselect": true,
        pagetype: "boolean",
        sort: params.query?.sort ? params.query?.sort : undefined,
        // variants: false,
        filter: [
          params.query?.sellingPrice
            ? `sellingPrice:[${params.query.sellingPrice?.gte} TO ${params.query.sellingPrice?.lte}]`
            : undefined,

          params.query?.purity
            ? params.query.purity
                .map((v: any) => `vPurity_uFilter:"${v}"`)
                .join(" OR ")
            : undefined,
          params.query?.fast_delivery !== undefined
            ? `vIsFastDelivery_uFilter:\"${params.query.fast_delivery}\"`
            : undefined,
          // params.query?.try_online !== undefined
          //   ? `vTryItOnline_uFilter:\"${params.query.try_online}\"`
          //   : undefined,
          params.query?.occasions
            ? params.query.occasions
                .map((v: any) => `vOccasions_uFilter:"${v}"`)
                .join(" OR ")
            : undefined,
        ].filter(Boolean),
        fields: [
          "title",
          "sku",
          "varientId",
          "productUrl",
          "imageUrl",
          "hasTryOnline",
          "discount",
          "discountLabel",
          "isBestSeller",
          "isNewArrival",
          "price",
          "sellingPrice",
          "defaultVariantId",
          "productId",
          "vIsFastDelivery",
        ].join(","),
      },
      paramsSerializer: (params: any) =>
        qs.stringify(params, { arrayFormat: "repeat" }),
      headers: {
        "unbxd-device-type": UNBXD_DEVICE,
      },
    });
    return data;
  }
);
// -----***unbxd***-----

export const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    setCategory: (
      state: { data: Category | null },
      action: PayloadAction<CategoryState["data"]>
    ) => {
      state.data = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        if (action.payload.category.data) {
          state.data = action.payload.category.data;
        }
      })
      .addCase(fetchCategoryBySlug.fulfilled, (state, action) => {
        state.data = action.payload;
      })
      .addCase(fetchCategoryMappingById.pending, (state, action) => {
        state.mapping = [];
      })
      .addCase(fetchCategoryMappingById.fulfilled, (state, action) => {
        state.mapping = action.payload;
        state.mappingLoading = false;
      })
      .addCase(fetchCategoryMappingById.rejected, (state, action) => {
        state.mapping = [];
        state.mappingLoading = false;
      })
      .addCase(fetchCategoryFiltersById.fulfilled, (state, action) => {
        state.filters = action.payload;
      })
      .addCase(fetchCategoryProductsById.pending, (state) => {
        state.productsLoading = true;
        state.products = null;
      })
      .addCase(fetchCategoryProductsById.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload;
      })
      .addCase(fetchCategoryProductsById.rejected, (state, action) => {
        state.productsLoading = false;
      })
      .addCase(fetchLoadMoreCategoryProductsById.fulfilled, (state, action) => {
        state.products = {
          ...action.payload,
          data: [...(state.products?.data || []), ...action.payload.data],
        };
      })
      .addCase(fetchSearchProducts.pending, (state) => {
        state.productsLoading = true;
        state.products = null;
      })
      .addCase(fetchSearchProducts.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload;
      })
      .addCase(fetchSearchFilters.fulfilled, (state, action) => {
        state.filters = action.payload;
      })
      .addCase(fetchSimilarProductFilters.fulfilled, (state, action) => {
        state.similarFilters = action.payload;
      })

      .addCase(fetchMoreSearchProducts.fulfilled, (state, action) => {
        state.products = {
          ...action.payload,
          data: [...(state.products?.data || []), ...action.payload.data],
        };
      })
      .addCase(fetchSimilarProducts.pending, (state, action) => {
        state.similarProducts = null;
      })
      .addCase(fetchSimilarProducts.fulfilled, (state, action) => {
        state.similarProducts = action.payload;
      })
      .addCase(fetchSearchProductBywizzy.pending, (state) => {
        state.productsLoading = true;
        state.products = null;
      })
      .addCase(fetchSearchProductBywizzy.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload?.payload;
        state.facets = action.payload?.payload?.facets;
      })
      .addCase(fetchSearchProductBywizzy.rejected, (state, action) => {
        state.productsLoading = false;
      })

      .addCase(fetchCategoryFiltersByIdWizzy.fulfilled, (state, action) => {
        state.products = action.payload?.payload;
        state.productsLoading = false;
        state.facets = action.payload?.payload?.facets;
      })
      .addCase(fetchMoreSearchProductsByWizzy.rejected, (state, action) => {
        state.productsLoading = false;
      })
      .addCase(fetchMoreSearchProductsByWizzy.fulfilled, (state, action) => {
        state.products = {
          ...action.payload,
          result: [
            ...(state.products?.result || []),
            ...action.payload?.payload?.result,
          ],
          filters: action.payload?.payload?.filters,
          total: action.payload?.payload?.total,
        };
      })
      .addCase(
        fetchLoadMoreCategoryProductsByIdWizzy.fulfilled,
        (state, action) => {
          state.products = {
            ...action.payload,
            result: [
              ...(state.products?.result || []),
              ...action.payload?.payload?.result,
            ],
            filters: action.payload?.payload?.filters,
            total: action.payload?.payload?.total,
          };
        }
      )
      .addCase(fetchSearchProductByunbxd.pending, (state) => {
        state.productsLoading = true;
        state.products = null;
      })
      .addCase(fetchSearchProductByunbxd.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload?.data;
        state.facets = action.payload?.data.facets;
        const payload = {
          query: action.payload?.data.searchMetaData?.queryParams?.q,
          requestId: action?.payload?.headers?.["unbxd-request-id"],
        };
        const Impressionpayload = {
          query: action.payload?.data.searchMetaData?.queryParams?.q,
          requestId: action?.payload?.headers?.["unbxd-request-id"],
          pids_list: action.payload?.data?.response?.products.map(
            (v: any) => v.sku
          ),
        };
        // unbxdTrackEvent("search", payload);
        // unbxdTrackEvent("search_impression", Impressionpayload);
      })
      .addCase(fetchSearchProductByunbxd.rejected, (state, action) => {
        console.log("err", state);
        state.productsLoading = false;
      })
      .addCase(fetchMoreSearchProductsByUnbxd.rejected, (state, action) => {
        state.productsLoading = false;
      })
      .addCase(fetchMoreSearchProductsByUnbxd.fulfilled, (state, action) => {
        state.products = {
          ...action.payload?.data,
          response: {
            ...action.payload?.data?.response,
            products: [
              ...(state.products?.response?.products || []),
              ...action.payload?.data?.response?.products,
            ],
          },
        };
        const Impressionpayload = {
          query: action.payload?.data.searchMetaData?.queryParams?.q,
          requestId: action?.payload?.headers?.["unbxd-request-id"],
          pids_list: action.payload?.data?.response?.products.map(
            (v: any) => v.sku
          ),
        };
        unbxdTrackEvent("search_impression", Impressionpayload);
      })
      .addCase(fetchCategoryFiltersByUnbxd.pending, (state) => {
        state.productsLoading = true;
        state.products = null;
      })

      .addCase(fetchCategoryFiltersByUnbxd.fulfilled, (state, action) => {
        console.log("category payload", action.payload);
        state.products = action.payload;
        state.productsLoading = false;
        state.facets = action.payload?.facets;
      })
      .addCase(fetchMoreCategoryProductsByUnbxd.rejected, (state, action) => {
        state.productsLoading = false;
      })
      .addCase(fetchMoreCategoryProductsByUnbxd.fulfilled, (state, action) => {
        state.products = {
          ...action.payload,
          response: {
            ...action?.payload.response,
            products: [
              ...(state.products?.response?.products || []),
              ...action.payload?.response?.products,
            ],
          },
        };
      });
  },
});

export const { setCategory } = categorySlice.actions;

export const selectCategory = (state: AppState) => state.category;

export default categorySlice.reducer;
